import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import MuiBreadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

// Routes whose URL has no parent segment but logically live under another page.
// Maps the first path segment to a synthetic crumb inserted before it.
const VIRTUAL_PARENTS = {
  payment_providers: { label: "Global Settings", to: "/global_settings" },
};

// Human labels for known route slugs. Anything not listed falls back to a
// title-cased version of the slug; raw ids are skipped entirely.
const LABELS = {
  restaurants: "Restaurants",
  "content-manager": "Content Manager",
  customers: "Customers",
  categories: "Categories",
  users: "Users",
  roles: "Roles",
  events: "Events",
  add_event: "Add Event",
  add_restaurant: "Add Restaurant",
  global_settings: "Global Settings",
  "ui-config-global": "UI Config Global",
  "table-ordering-menu-layout": "Table Ordering Menu Layout",
  features_list: "Features",
  ar_models: "AR Models",
  model: "Model",
  select_menu: "Select Menu",
  add_category: "Add Category",
  add_menu: "Add Menu",
  manage_menu: "Manage Menu",
  category_reorder: "Reorder Categories",
  settings: "Settings",
  menu: "Menu",
  category: "Category",
  info: "Info",
  custom_info: "Custom Info",
  account_detail: "Account Detail",
  order_history: "Order History",
  sales: "Sales",
  x_report: "X Report",
  z_report: "Z Report",
  manage_staffs: "Staff Management",
  floor_management: "Floor Management",
  gift_card: "Gift Cards",
  transactions: "Transactions",
  subscriptions: "Subscriptions",
  "content-generations": "Content Generations",
  "reference-content-manager": "Reference Content Manager",
  "content-manager": "Content Manager",
  dashboard: "Dashboard",
  socials: "Socials",
  table_setup_settings: "Table Setup",
  "table-booking-settings": "Table Booking",
  "giftcard-settings": "Gift Card Settings",
  stripe_settings: "Stripe Settings",
  takeaway_settings: "Takeaway Settings",
  charges: "Charges",
  printers: "Printers",
  course: "Course",
  modifiers: "Modifiers",
  coupons: "Coupons",
  discounts: "Discounts",
  allergens: "Allergens",
  "menu-attributes": "Menu Attributes",
  foodo_menu: "Foodo Menu",
  uber_eats: "UberEats",
  swiggy: "Swiggy",
  version: "Version",
  payment_providers: "Payment Providers",
  add: "Add",
  edit: "Edit",
  new: "New",
};

// Mongo ObjectId or pure numbers => treat as an opaque id (skipped in crumbs).
// NOTE: don't add a length heuristic here — real kebab slugs like
// "table-ordering-menu-layout" exceed 20 chars and must NOT be skipped.
const isId = (s) => /^[0-9a-f]{24}$/i.test(s) || /^\d+$/.test(s);

const titleCase = (s) =>
  s.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const Breadcrumbs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const restaurant = useSelector((s) => s.userData.restaurantData);

  const segs = location.pathname.split("/").filter(Boolean);
  if (segs.length === 0 || segs[0] === "login" || segs[0] === "logout") {
    return null;
  }

  const crumbs = [];
  if (segs.length > 0 && VIRTUAL_PARENTS[segs[0]]) {
    crumbs.push(VIRTUAL_PARENTS[segs[0]]);
  }
  for (let i = 0; i < segs.length; i++) {
    const seg = segs[i];
    const to = "/" + segs.slice(0, i + 1).join("/");

    // The "/restaurant/:id" prefix: drop the literal "restaurant" and show the
    // restaurant's name (linking to its settings page) instead of the raw id.
    if (seg === "restaurant") continue;
    if (i > 0 && segs[i - 1] === "restaurant") {
      const name =
        restaurant && restaurant._id === seg ? restaurant.name : "Restaurant";
      crumbs.push({ label: name, to });
      continue;
    }

    if (isId(seg)) continue; // skip opaque ids (model / menu / coupon ids)
    crumbs.push({ label: LABELS[seg] || titleCase(seg), to });
  }

  if (crumbs.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <MuiBreadcrumbs
        aria-label="breadcrumb"
        separator={
          <NavigateNextIcon sx={{ fontSize: 16, color: "#9aa39a" }} />
        }
      >
        <Link
          component="button"
          underline="none"
          onClick={() => navigate("/")}
          aria-label="Home"
          sx={styles.home}
        >
          <HomeOutlinedIcon sx={{ fontSize: 18 }} />
        </Link>

        {crumbs.map((c, i) => {
          const isLast = i === crumbs.length - 1;
          return isLast ? (
            <Typography key={c.to} sx={styles.current}>
              {c.label}
            </Typography>
          ) : (
            <Link
              key={c.to}
              component="button"
              underline="none"
              onClick={() => navigate(c.to)}
              sx={styles.link}
            >
              {c.label}
            </Link>
          );
        })}
      </MuiBreadcrumbs>
    </Box>
  );
};

export default Breadcrumbs;

const styles = {
  home: {
    display: "flex",
    alignItems: "center",
    color: "#9aa39a",
    "&:hover": { color: "#053418" },
  },
  link: {
    fontFamily: "GilroySemiBold",
    fontSize: 13,
    color: "#6b7368",
    cursor: "pointer",
    "&:hover": { color: "#053418" },
  },
  current: {
    fontFamily: "GilroyBold",
    fontSize: 13,
    color: "#053418",
  },
};
